import './Modal.scss';
import clsx from 'clsx';
import React, {
  memo,
  PropsWithChildren,
  useContext,
  useEffect,
  useLayoutEffect,
} from 'react';
import ReactModal, { Props as ModalProps } from 'react-modal';
import { RuntimeContext } from 'src/runtimeContext';

type Props = PropsWithChildren<{
  className?: string;
  opened: boolean;
  size?: 'sm' | 'md' | 'lg';
  fullScreen?: boolean;
  fullHeight?: boolean;
  handleClose: () => void;
} & Omit<
ModalProps,
'className' |
'isOpen' |
'onRequestClose' |
'appElement' |
'style'
>>;

export const Modal = memo((
  {
    opened,
    fullScreen,
    fullHeight,
    size = 'md',
    handleClose,
    children,
    className,
    ...props
  }: Props,
) => {
  const { modalRoot } = useContext(RuntimeContext);

  useEffect(() => {
    document.body.classList.toggle('bp-overflow-hidden', opened);

    return (): void => {
      document.body.classList.remove('bp-overflow-hidden');
    };
  }, [opened]);

  useLayoutEffect(() => {
    const scrollbarWidth = opened ? window.innerWidth - document.documentElement.clientWidth : 0;

    document.documentElement.style.setProperty('--scroll-bar-width', `${scrollbarWidth}px`);
  }, [opened]);

  return (
    <ReactModal
      {...props}
      className={clsx({
        'bp-modal': true,
        [`bp-modal--${size}`]: true,
        'bp-modal--full-screen': fullScreen,
        'bp-modal--full-height': fullHeight,
      }, className)}
      isOpen={opened}
      onRequestClose={handleClose}
      appElement={modalRoot}
      overlayClassName="bp-modal-overlay"
    >{children}
    </ReactModal>
  );
});
